import {
  AGENT_WORKFLOW_SAMPLE_RUNS,
  AGENT_WORKFLOW_TEMPLATES,
  agentWorkflowRuntimeTargetLabel,
  agentWorkflowRuntimeTargetRunsOffline,
  type AgentWorkflowApprovalDecision,
  type AgentWorkflowDefinition,
  type AgentWorkflowRunEvent,
  type AgentWorkflowRunRecord,
  type AgentWorkflowSchedule,
  type AgentWorkflowStepDefinition,
  type AgentWorkflowTenantStore,
} from "@t3tools/shared/agentWorkflow";
import type { EnvironmentId } from "@t3tools/contracts";
import { useAtomValue } from "@effect/atom-react";
import * as Option from "effect/Option";
import { AsyncResult } from "effect/unstable/reactivity";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Box,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Cloud,
  MessageSquare,
  PauseCircle,
  Play,
  Plus,
  Repeat,
  Server,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";

import { squashAtomCommandFailure } from "@t3tools/client-runtime/state/runtime";

import { cn } from "~/lib/utils";
import { useAtomCommand } from "~/state/use-atom-command";
import { serverEnvironment } from "~/state/server";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Empty, EmptyDescription, EmptyTitle } from "./ui/empty";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Select, SelectButton, SelectItem, SelectPopup } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

interface WorkflowSidebarProps {
  mode?: "sheet" | "sidebar" | "embedded";
  activeProjectName: string | null | undefined;
  environmentId: EnvironmentId;
  tenantId: string;
}

type PanelView = "workflows" | "activity";
type RunStatusFilter =
  | "all"
  | "active"
  | "waiting-for-approval"
  | "completed"
  | "failed"
  | "cancelled";

const TWO_COLUMN_MIN_WIDTH = 640;

const SCHEDULE_PRESETS: ReadonlyArray<{
  readonly id: string;
  readonly schedule: AgentWorkflowSchedule;
}> = [
  { id: "manual", schedule: { kind: "manual", label: "Manual" } },
  {
    id: "daily-0700",
    schedule: {
      kind: "cron",
      expression: "0 7 * * *",
      timezone: "Europe/Berlin",
      label: "Daily 07:00",
    },
  },
  {
    id: "daily-0800",
    schedule: {
      kind: "cron",
      expression: "0 8 * * *",
      timezone: "Europe/Berlin",
      label: "Daily 08:00",
    },
  },
  {
    id: "weekdays-0900",
    schedule: {
      kind: "cron",
      expression: "0 9 * * 1-5",
      timezone: "Europe/Berlin",
      label: "Weekdays 09:00",
    },
  },
  {
    id: "monday-0730",
    schedule: {
      kind: "cron",
      expression: "30 7 * * 1",
      timezone: "Europe/Berlin",
      label: "Monday 07:30",
    },
  },
];

const RUN_STATUS_FILTERS: ReadonlyArray<{ id: RunStatusFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "waiting-for-approval", label: "Waiting" },
  { id: "completed", label: "Done" },
  { id: "failed", label: "Failed" },
  { id: "cancelled", label: "Cancelled" },
];

function workflowStatusLabel(status: AgentWorkflowDefinition["status"]): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "paused":
      return "Paused";
    case "ready":
      return "Ready";
    case "archived":
      return "Archived";
  }
}

function workflowStatusDotClass(status: AgentWorkflowDefinition["status"]): string {
  switch (status) {
    case "ready":
      return "bg-emerald-500";
    case "paused":
      return "bg-amber-500";
    case "draft":
      return "bg-blue-500";
    case "archived":
      return "bg-muted-foreground/50";
  }
}

function runStatusPresentation(status: AgentWorkflowRunRecord["status"]): {
  icon: typeof CheckCircle2;
  label: string;
  className: string;
} {
  switch (status) {
    case "completed":
      return {
        icon: CheckCircle2,
        label: "Completed",
        className: "text-emerald-600 dark:text-emerald-400",
      };
    case "waiting-for-approval":
      return {
        icon: AlertCircle,
        label: "Waiting",
        className: "text-amber-600 dark:text-amber-400",
      };
    case "queued":
      return {
        icon: Clock3,
        label: "Queued",
        className: "text-blue-600 dark:text-blue-400",
      };
    case "running":
      return {
        icon: Clock3,
        label: "Running",
        className: "text-blue-600 dark:text-blue-400",
      };
    case "failed":
      return {
        icon: AlertCircle,
        label: "Failed",
        className: "text-destructive",
      };
    case "cancelled":
      return {
        icon: PauseCircle,
        label: "Cancelled",
        className: "text-muted-foreground",
      };
  }
}

function stepKindPresentation(kind: AgentWorkflowStepDefinition["kind"]): {
  icon: typeof Wrench;
  label: string;
} {
  switch (kind) {
    case "werkflow-tool":
      return { icon: Wrench, label: "ERP tool" };
    case "agent":
      return { icon: Sparkles, label: "Agent" };
    case "approval":
      return { icon: ShieldCheck, label: "Approval" };
    case "sandbox":
      return { icon: Box, label: "Sandbox" };
  }
}

function runEventPresentation(type: AgentWorkflowRunEvent["type"]): {
  icon: typeof Play;
  className: string;
} {
  switch (type) {
    case "started":
      return { icon: Play, className: "text-blue-600 dark:text-blue-400" };
    case "tool-call":
      return { icon: Wrench, className: "text-muted-foreground" };
    case "approval-requested":
      return { icon: ShieldCheck, className: "text-amber-600 dark:text-amber-400" };
    case "approval-resolved":
      return { icon: CheckCircle2, className: "text-emerald-600 dark:text-emerald-400" };
    case "result":
      return { icon: CheckCircle2, className: "text-emerald-600 dark:text-emerald-400" };
    case "error":
      return { icon: AlertCircle, className: "text-destructive" };
  }
}

function scheduleExpressionLabel(workflow: AgentWorkflowDefinition): string {
  if (workflow.schedule.kind === "manual") return "Manual";
  return workflow.schedule.expression ?? workflow.schedule.kind;
}

function schedulePresetId(schedule: AgentWorkflowSchedule): string {
  return (
    SCHEDULE_PRESETS.find(
      (preset) =>
        preset.schedule.kind === schedule.kind &&
        preset.schedule.expression === schedule.expression &&
        preset.schedule.timezone === schedule.timezone,
    )?.id ?? "custom"
  );
}

function workflowNeedsApproval(workflow: AgentWorkflowDefinition): boolean {
  return (
    workflow.steps.some((step) => step.kind === "approval") ||
    workflow.security.approvalRequiredForCustomerFacingActions ||
    workflow.security.approvalRequiredForWrites
  );
}

function formatRelativeTime(iso: string | undefined): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const deltaSeconds = Math.round((target - Date.now()) / 1000);
  const absSeconds = Math.abs(deltaSeconds);
  const units: ReadonlyArray<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
    [604800, "day"],
    [2629800, "week"],
    [31557600, "month"],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (let index = 0; index < units.length; index += 1) {
    const [limit, unit] = units[index]!;
    if (absSeconds < limit) {
      const divisor = index === 0 ? 1 : units[index - 1]![0];
      return formatter.format(Math.round(deltaSeconds / divisor), unit);
    }
  }
  return formatter.format(Math.round(deltaSeconds / 31557600), "year");
}

function formatDuration(
  startedAt: string | undefined,
  completedAt: string | undefined,
): string | null {
  if (!startedAt || !completedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

function formatClock(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function isAgentWorkflowTenantStore(value: unknown): value is AgentWorkflowTenantStore {
  return (
    typeof value === "object" &&
    value !== null &&
    "definitions" in value &&
    Array.isArray(value.definitions) &&
    "runs" in value &&
    Array.isArray(value.runs)
  );
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function SegmentedControl<T extends string>(props: {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-border/70 bg-muted/30 p-0.5",
        props.className,
      )}
    >
      {props.options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => props.onChange(option.id)}
          className={cn(
            "rounded px-2.5 py-1 text-xs font-medium transition",
            props.value === option.id
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SecurityChip(props: {
  icon: typeof ShieldCheck;
  label: string;
  tone?: "muted" | "warning";
}) {
  const Icon = props.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px]",
        props.tone === "warning"
          ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "border-border/70 bg-muted/40 text-muted-foreground",
      )}
    >
      <Icon className="size-3" />
      {props.label}
    </span>
  );
}

function SectionHeader(props: { label: string; trailing?: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/50 uppercase">
        {props.label}
      </p>
      {props.trailing ? (
        <span className="text-[11px] text-muted-foreground/60">{props.trailing}</span>
      ) : null}
    </div>
  );
}

const WorkflowSidebar = memo(function WorkflowSidebar({
  mode = "sidebar",
  activeProjectName,
  environmentId,
  tenantId,
}: WorkflowSidebarProps) {
  const workflowStoreResult = useAtomValue(
    serverEnvironment.agentWorkflowStore({
      environmentId,
      input: { tenantId },
    }),
  );
  const workflowUpdatesResult = useAtomValue(
    serverEnvironment.agentWorkflowRunUpdates({
      environmentId,
      input: { tenantId },
    }),
  );
  const queriedStore = Option.getOrNull(AsyncResult.value(workflowStoreResult));
  const pushedStore = Option.getOrNull(AsyncResult.value(workflowUpdatesResult));
  const storeLoaded = isAgentWorkflowTenantStore(queriedStore);
  const [localStore, setLocalStore] = useState<AgentWorkflowTenantStore | null>(null);
  const [view, setView] = useState<PanelView>("workflows");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [showDetailOnNarrow, setShowDetailOnNarrow] = useState(false);
  const [activityFilter, setActivityFilter] = useState<RunStatusFilter>("all");
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerName, setComposerName] = useState("");
  const [composerText, setComposerText] = useState("");
  const [composerScheduleId, setComposerScheduleId] = useState<string>(SCHEDULE_PRESETS[0]!.id);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [busyRunId, setBusyRunId] = useState<string | null>(null);
  const [convexDeployNotice, setConvexDeployNotice] = useState<string | null>(null);
  const [updatingWorkflowId, setUpdatingWorkflowId] = useState<string | null>(null);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();
  const twoColumn = containerWidth >= TWO_COLUMN_MIN_WIDTH;

  const updateSchedule = useAtomCommand(serverEnvironment.updateAgentWorkflowSchedule, {
    label: "workflow schedule update",
    reportFailure: true,
  });
  const runWorkflowOnce = useAtomCommand(serverEnvironment.runAgentWorkflowOnce, {
    label: "workflow run once",
    reportFailure: true,
  });
  const createAgentWorkflow = useAtomCommand(serverEnvironment.createAgentWorkflow, {
    label: "workflow create",
    reportFailure: true,
  });
  const resolveApproval = useAtomCommand(serverEnvironment.resolveAgentWorkflowApproval, {
    label: "workflow approval resolve",
    reportFailure: true,
  });
  const cancelRun = useAtomCommand(serverEnvironment.cancelAgentWorkflowRun, {
    label: "workflow run cancel",
    reportFailure: true,
  });
  const validateAgentWorkflow = useAtomCommand(serverEnvironment.validateAgentWorkflow, {
    label: "workflow Convex validation",
    reportFailure: true,
  });
  const approveAgentWorkflowVersion = useAtomCommand(
    serverEnvironment.approveAgentWorkflowVersion,
    { label: "workflow Convex approval", reportFailure: true },
  );
  const deployAgentWorkflowVersion = useAtomCommand(serverEnvironment.deployAgentWorkflowVersion, {
    label: "workflow Convex deployment",
    reportFailure: true,
  });
  const setConvexWorkflowSecret = useAtomCommand(serverEnvironment.setConvexWorkflowSecret, {
    label: "Convex deployment secret",
    reportFailure: true,
  });
  const convexWorkflowSecretStatus = useAtomCommand(serverEnvironment.convexWorkflowSecretStatus, {
    label: "Convex deployment secret status",
    reportFailure: true,
  });

  useEffect(() => {
    if (isAgentWorkflowTenantStore(queriedStore)) {
      setLocalStore(queriedStore);
    }
  }, [queriedStore]);

  useEffect(() => {
    if (isAgentWorkflowTenantStore(pushedStore)) {
      setLocalStore(pushedStore);
    }
  }, [pushedStore]);

  const definitions = useMemo(
    () => localStore?.definitions ?? AGENT_WORKFLOW_TEMPLATES,
    [localStore?.definitions],
  );
  const runs = useMemo(() => localStore?.runs ?? AGENT_WORKFLOW_SAMPLE_RUNS, [localStore?.runs]);

  const runsByWorkflowId = useMemo(() => {
    const map = new Map<string, AgentWorkflowRunRecord[]>();
    for (const run of runs) {
      const existing = map.get(run.workflowId);
      if (existing) existing.push(run);
      else map.set(run.workflowId, [run]);
    }
    return map;
  }, [runs]);

  useEffect(() => {
    if (definitions.length === 0) {
      setSelectedWorkflowId(null);
      return;
    }
    setSelectedWorkflowId((current) =>
      current && definitions.some((workflow) => workflow.id === current)
        ? current
        : definitions[0]!.id,
    );
  }, [definitions]);

  const selectedWorkflow = useMemo(
    () => definitions.find((workflow) => workflow.id === selectedWorkflowId) ?? null,
    [definitions, selectedWorkflowId],
  );

  const applyStoreResult = (result: { _tag: string; value?: unknown }) => {
    if (result._tag === "Success" && isAgentWorkflowTenantStore(result.value)) {
      setLocalStore(result.value);
      return result.value;
    }
    return null;
  };

  const persistSchedule = async (
    workflow: AgentWorkflowDefinition,
    schedule: AgentWorkflowSchedule,
  ) => {
    setUpdatingWorkflowId(workflow.id);
    const result = await updateSchedule({
      environmentId,
      input: { tenantId, workflowId: workflow.id, schedule },
    });
    setUpdatingWorkflowId(null);
    applyStoreResult(result);
  };

  const runOnce = async (workflow: AgentWorkflowDefinition) => {
    setRunningWorkflowId(workflow.id);
    const result = await runWorkflowOnce({
      environmentId,
      input: { tenantId, workflowId: workflow.id },
    });
    setRunningWorkflowId(null);
    applyStoreResult(result);
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setComposerName("");
    setComposerText("");
    setComposerScheduleId(SCHEDULE_PRESETS[0]!.id);
    setComposerError(null);
  };

  const createWorkflow = async () => {
    const name = composerName.trim();
    const instruction = composerText.trim();
    if (!name) {
      setComposerError("Bitte einen Namen für den Workflow angeben.");
      return;
    }
    if (!instruction) {
      setComposerError("Bitte eine Anweisung für den Workflow angeben.");
      return;
    }
    const schedule =
      SCHEDULE_PRESETS.find((preset) => preset.id === composerScheduleId)?.schedule ??
      SCHEDULE_PRESETS[0]!.schedule;
    setComposerError(null);
    setCreating(true);
    const result = await createAgentWorkflow({
      environmentId,
      input: { tenantId, name, instruction, schedule },
    });
    setCreating(false);
    if (result._tag === "Failure") {
      const failure = squashAtomCommandFailure(result);
      setComposerError(
        failure instanceof Error ? failure.message : "Workflow konnte nicht erstellt werden.",
      );
      return;
    }
    const next = applyStoreResult(result);
    if (next) {
      closeComposer();
      const created = next.definitions[0];
      if (created) {
        setSelectedWorkflowId(created.id);
        setView("workflows");
        setShowDetailOnNarrow(true);
      }
    }
  };

  const resolveRun = async (
    run: AgentWorkflowRunRecord,
    decision: AgentWorkflowApprovalDecision,
  ) => {
    setBusyRunId(run.id);
    const result = await resolveApproval({
      environmentId,
      input: { tenantId, runId: run.id, decision },
    });
    setBusyRunId(null);
    applyStoreResult(result);
  };

  const validateConvex = async (workflow: AgentWorkflowDefinition) => {
    const result = await validateAgentWorkflow({
      environmentId,
      input: { tenantId, workflowId: workflow.id },
    });
    applyStoreResult(result);
  };
  const approveConvex = async (workflow: AgentWorkflowDefinition) => {
    if (!workflow.convex) return;
    const result = await approveAgentWorkflowVersion({
      environmentId,
      input: { tenantId, workflowId: workflow.id, sourceVersion: workflow.convex.sourceVersion },
    });
    applyStoreResult(result);
  };
  const asSecretStatus = (result: { _tag: string; value?: unknown }): ConvexSecretStatus | null =>
    result._tag === "Success" && result.value && typeof result.value === "object"
      ? (result.value as ConvexSecretStatus)
      : null;
  const setConvexSecret = async (
    deploymentRef: string,
    kind: ConvexSecretKind,
    value: string,
  ): Promise<ConvexSecretStatus | null> => {
    const result = await setConvexWorkflowSecret({
      environmentId,
      input: { deploymentRef, kind, value },
    });
    return asSecretStatus(result);
  };
  const loadConvexSecretStatus = async (
    deploymentRef: string,
  ): Promise<ConvexSecretStatus | null> => {
    const result = await convexWorkflowSecretStatus({ environmentId, input: { deploymentRef } });
    return asSecretStatus(result);
  };
  const deployConvex = async (workflow: AgentWorkflowDefinition) => {
    if (!workflow.convex) return;
    // Presence-gate the deploy so the operator gets a clear message instead of a
    // deep server error when a deployment secret has not been configured yet.
    const status = await loadConvexSecretStatus(workflow.convex.deploymentRef || "default");
    if (status && (!status.deployKey || !status.runtimeToken || !status.callbackSecret)) {
      const missing = [
        status.deployKey ? null : "deploy key",
        status.runtimeToken ? null : "runtime token",
        status.callbackSecret ? null : "callback secret",
      ].filter((item): item is string => item !== null);
      setConvexDeployNotice(
        `Configure the Convex deployment secrets first: ${missing.join(", ")}.`,
      );
      return;
    }
    setConvexDeployNotice(null);
    const result = await deployAgentWorkflowVersion({
      environmentId,
      input: { tenantId, workflowId: workflow.id, sourceVersion: workflow.convex.sourceVersion },
    });
    applyStoreResult(result);
  };

  const cancelRunById = async (run: AgentWorkflowRunRecord) => {
    setBusyRunId(run.id);
    const result = await cancelRun({
      environmentId,
      input: { tenantId, runId: run.id },
    });
    setBusyRunId(null);
    applyStoreResult(result);
  };

  const selectWorkflow = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setShowDetailOnNarrow(true);
  };

  const activityRuns = useMemo(
    () =>
      activityFilter === "all"
        ? runs
        : activityFilter === "active"
          ? runs.filter((run) => run.status === "queued" || run.status === "running")
          : runs.filter((run) => run.status === activityFilter),
    [runs, activityFilter],
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex min-h-0 flex-col bg-card/50",
        mode === "sidebar"
          ? "h-full w-[360px] shrink-0 border-l border-border/70"
          : "h-full w-full",
      )}
    >
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Badge
            variant="info"
            size="sm"
            className="rounded-md px-1.5 py-0 font-semibold tracking-wide uppercase"
          >
            Workflows
          </Badge>
          <span className="truncate text-[11px] text-muted-foreground/70">
            {activeProjectName ?? tenantId}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 text-xs"
          onClick={() => (composerOpen ? closeComposer() : setComposerOpen(true))}
        >
          <Plus className="size-3.5" />
          New
        </Button>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/50 px-3 py-2">
        <SegmentedControl
          options={[
            { id: "workflows", label: "Workflows" },
            { id: "activity", label: "Activity" },
          ]}
          value={view}
          onChange={setView}
        />
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
          <Server className="size-3.5" />
          Codex runtime
        </span>
      </div>

      {composerOpen ? (
        <div className="shrink-0 space-y-2 border-b border-border/50 bg-background/40 p-3">
          <div className="space-y-1">
            <label
              className="text-[11px] font-medium text-muted-foreground"
              htmlFor="workflow-name"
            >
              Name
            </label>
            <Input
              id="workflow-name"
              value={composerName}
              onChange={(event) => {
                setComposerName(event.target.value);
                if (composerError) setComposerError(null);
              }}
              placeholder="e.g. Chase overdue invoices"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label
              className="text-[11px] font-medium text-muted-foreground"
              htmlFor="workflow-instruction"
            >
              Instruction
            </label>
            <textarea
              id="workflow-instruction"
              value={composerText}
              onChange={(event) => {
                setComposerText(event.target.value);
                if (composerError) setComposerError(null);
              }}
              rows={3}
              placeholder="Describe what this workflow should do…"
              className="w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Schedule</label>
            <Select
              value={composerScheduleId}
              onValueChange={(value) => {
                if (value !== null) setComposerScheduleId(value);
              }}
            >
              <SelectButton size="xs" className="h-8 w-full justify-between text-xs">
                {SCHEDULE_PRESETS.find((preset) => preset.id === composerScheduleId)?.schedule
                  .label ?? "Manual"}
              </SelectButton>
              <SelectPopup>
                {SCHEDULE_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.schedule.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </div>
          {composerError ? <p className="text-[11px] text-destructive">{composerError}</p> : null}
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={closeComposer}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={
                creating || composerName.trim().length === 0 || composerText.trim().length === 0
              }
              onClick={() => void createWorkflow()}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </div>
        </div>
      ) : null}

      {!storeLoaded && !localStore ? (
        <WorkflowSkeleton />
      ) : view === "activity" ? (
        <ActivityView
          runs={activityRuns}
          definitions={definitions}
          filter={activityFilter}
          onFilterChange={setActivityFilter}
          busyRunId={busyRunId}
          onResolve={resolveRun}
          onCancel={cancelRunById}
          storeLoaded={storeLoaded}
        />
      ) : definitions.length === 0 ? (
        <Empty>
          <EmptyTitle className="text-base">No workflows yet</EmptyTitle>
          <EmptyDescription>
            Create your first ERP automation with the New button above.
          </EmptyDescription>
        </Empty>
      ) : (
        <div className="flex min-h-0 flex-1">
          {(twoColumn || !showDetailOnNarrow) && (
            <WorkflowList
              className={twoColumn ? "w-[280px] shrink-0 border-r border-border/60" : "flex-1"}
              definitions={definitions}
              runsByWorkflowId={runsByWorkflowId}
              selectedWorkflowId={selectedWorkflowId}
              onSelect={selectWorkflow}
            />
          )}
          {(twoColumn || showDetailOnNarrow) &&
            (selectedWorkflow ? (
              <WorkflowDetail
                className="min-w-0 flex-1"
                workflow={selectedWorkflow}
                runs={runsByWorkflowId.get(selectedWorkflow.id) ?? []}
                showBack={!twoColumn}
                onBack={() => setShowDetailOnNarrow(false)}
                updating={updatingWorkflowId === selectedWorkflow.id}
                running={runningWorkflowId === selectedWorkflow.id}
                busyRunId={busyRunId}
                onEditSchedule={(schedule) => void persistSchedule(selectedWorkflow, schedule)}
                onRunOnce={() => void runOnce(selectedWorkflow)}
                onResolve={resolveRun}
                onCancel={cancelRunById}
                onValidateConvex={validateConvex}
                onApproveConvex={approveConvex}
                onDeployConvex={deployConvex}
                onSetConvexSecret={setConvexSecret}
                onLoadConvexSecretStatus={loadConvexSecretStatus}
                convexDeployNotice={convexDeployNotice}
              />
            ) : null)}
        </div>
      )}
    </div>
  );
});

function WorkflowSkeleton() {
  return (
    <div className="space-y-3 p-3">
      {[0, 1, 2].map((index) => (
        <div key={index} className="rounded-lg border border-border/60 p-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-2 h-3 w-24" />
          <Skeleton className="mt-3 h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

function WorkflowList(props: {
  className?: string;
  definitions: ReadonlyArray<AgentWorkflowDefinition>;
  runsByWorkflowId: ReadonlyMap<string, AgentWorkflowRunRecord[]>;
  selectedWorkflowId: string | null;
  onSelect: (workflowId: string) => void;
}) {
  return (
    <ScrollArea className={cn("min-h-0", props.className)}>
      <div className="space-y-1.5 p-2">
        {props.definitions.map((workflow) => {
          const workflowRuns = props.runsByWorkflowId.get(workflow.id) ?? [];
          const lastRun = workflowRuns[0];
          const nextRunLabel =
            workflow.schedule.kind === "manual"
              ? "Manual"
              : (formatRelativeTime(lastRun?.scheduledFor) ?? workflow.schedule.label);
          const active = workflow.id === props.selectedWorkflowId;
          return (
            <button
              key={workflow.id}
              type="button"
              onClick={() => props.onSelect(workflow.id)}
              className={cn(
                "w-full rounded-lg border px-3 py-2.5 text-left transition",
                active
                  ? "border-border bg-accent/60"
                  : "border-border/60 bg-background/40 hover:border-border hover:bg-accent/30",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    workflowStatusDotClass(workflow.status),
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {workflow.name}
                </span>
                {workflowNeedsApproval(workflow) ? (
                  <ShieldCheck className="size-3.5 shrink-0 text-muted-foreground/70" />
                ) : null}
              </div>
              <div className="mt-1 flex items-center gap-2 pl-4 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="size-3" />
                  {workflow.schedule.label}
                </span>
                <span className="text-muted-foreground/40">·</span>
                <span>{nextRunLabel}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 pl-4">
                {workflow.steps.map((step) => {
                  const presentation = stepKindPresentation(step.kind);
                  const Icon = presentation.icon;
                  return (
                    <Tooltip key={step.id}>
                      <TooltipTrigger
                        render={
                          <span className="inline-flex size-4 items-center justify-center text-muted-foreground/60">
                            <Icon className="size-3" />
                          </span>
                        }
                      />
                      <TooltipPopup>{`${presentation.label}: ${step.title}`}</TooltipPopup>
                    </Tooltip>
                  );
                })}
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground/70">
                  {workflowRuns.length > 0 && lastRun ? (
                    <RunStatusDot status={lastRun.status} />
                  ) : null}
                  {workflowRuns.length} {workflowRuns.length === 1 ? "run" : "runs"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </ScrollArea>
  );
}

function RunStatusDot(props: { status: AgentWorkflowRunRecord["status"] }) {
  const presentation = runStatusPresentation(props.status);
  const Icon = presentation.icon;
  return <Icon className={cn("size-3", presentation.className)} />;
}

type ConvexSecretKind = "deploy-key" | "runtime-token" | "callback-secret";
interface ConvexSecretStatus {
  readonly deployKey: boolean;
  readonly runtimeToken: boolean;
  readonly callbackSecret: boolean;
}

const CONVEX_SECRET_FIELDS: ReadonlyArray<{
  readonly kind: ConvexSecretKind;
  readonly label: string;
  readonly statusKey: keyof ConvexSecretStatus;
}> = [
  { kind: "deploy-key", label: "Deploy key", statusKey: "deployKey" },
  { kind: "runtime-token", label: "Runtime token", statusKey: "runtimeToken" },
  { kind: "callback-secret", label: "Callback secret", statusKey: "callbackSecret" },
];

function ConvexDeploymentSecrets(props: {
  deploymentRef: string;
  onSetSecret: (
    deploymentRef: string,
    kind: ConvexSecretKind,
    value: string,
  ) => Promise<ConvexSecretStatus | null>;
  onLoadStatus: (deploymentRef: string) => Promise<ConvexSecretStatus | null>;
}) {
  const [status, setStatus] = useState<ConvexSecretStatus | null>(null);
  const [drafts, setDrafts] = useState<Record<ConvexSecretKind, string>>({
    "deploy-key": "",
    "runtime-token": "",
    "callback-secret": "",
  });
  const [busyKind, setBusyKind] = useState<ConvexSecretKind | null>(null);

  useEffect(() => {
    let active = true;
    void props.onLoadStatus(props.deploymentRef).then((result) => {
      if (active) setStatus(result);
    });
    return () => {
      active = false;
    };
  }, [props.deploymentRef]);

  const submit = async (kind: ConvexSecretKind) => {
    const value = drafts[kind].trim();
    if (!value) return;
    setBusyKind(kind);
    const next = await props.onSetSecret(props.deploymentRef, kind, value);
    setBusyKind(null);
    if (next) setStatus(next);
    setDrafts((prev) => ({ ...prev, [kind]: "" }));
  };

  return (
    <div className="mt-3 rounded-md border border-border/60 p-2">
      <p className="text-xs font-medium">Convex deployment secrets</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Deployment ref <span className="font-mono">{props.deploymentRef}</span>. All three are
        required before deploy. Values are write-only and stored in the server secret store.
      </p>
      <div className="mt-2 space-y-2">
        {CONVEX_SECRET_FIELDS.map((field) => {
          const configured = status?.[field.statusKey] ?? false;
          return (
            <div key={field.kind} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-[11px]">{field.label}</span>
              <span
                className={cn("text-[11px]", configured ? "text-emerald-500" : "text-destructive")}
              >
                {configured ? "set" : "missing"}
              </span>
              <Input
                type="password"
                className="h-7 flex-1 text-xs"
                placeholder={configured ? "Replace…" : "Enter value…"}
                value={drafts[field.kind]}
                onChange={(event) =>
                  setDrafts((prev) => ({ ...prev, [field.kind]: event.target.value }))
                }
              />
              <Button
                size="xs"
                variant="outline"
                disabled={busyKind === field.kind || drafts[field.kind].trim().length === 0}
                onClick={() => void submit(field.kind)}
              >
                Save
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WorkflowDetail(props: {
  className?: string;
  workflow: AgentWorkflowDefinition;
  runs: ReadonlyArray<AgentWorkflowRunRecord>;
  showBack: boolean;
  onBack: () => void;
  updating: boolean;
  running: boolean;
  busyRunId: string | null;
  onEditSchedule: (schedule: AgentWorkflowSchedule) => void;
  onRunOnce: () => void;
  onResolve: (run: AgentWorkflowRunRecord, decision: AgentWorkflowApprovalDecision) => void;
  onCancel: (run: AgentWorkflowRunRecord) => void;
  onValidateConvex: (workflow: AgentWorkflowDefinition) => void;
  onApproveConvex: (workflow: AgentWorkflowDefinition) => void;
  onDeployConvex: (workflow: AgentWorkflowDefinition) => void;
  onSetConvexSecret: (
    deploymentRef: string,
    kind: ConvexSecretKind,
    value: string,
  ) => Promise<ConvexSecretStatus | null>;
  onLoadConvexSecretStatus: (deploymentRef: string) => Promise<ConvexSecretStatus | null>;
  convexDeployNotice: string | null;
}) {
  const { workflow } = props;
  const { security } = workflow;
  return (
    <ScrollArea className={cn("min-h-0", props.className)}>
      <div className="space-y-4 p-3">
        <div>
          {props.showBack ? (
            <button
              type="button"
              onClick={props.onBack}
              className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              All workflows
            </button>
          ) : null}
          <div className="flex items-start justify-between gap-3">
            <h2 className="min-w-0 text-sm font-semibold text-foreground">{workflow.name}</h2>
            <Badge variant="secondary" size="sm" className="shrink-0 rounded-md">
              {workflowStatusLabel(workflow.status)}
            </Badge>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {workflow.source} · v{workflow.version} ·{" "}
            {agentWorkflowRuntimeTargetLabel(security.runtimeTarget)}
          </p>
          {workflow.executionKind === "convex" ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <SecurityChip icon={Server} label="Convex deterministic" />
              <SecurityChip icon={Clock3} label={workflow.convex?.deploymentStatus ?? "draft"} />
              {workflow.convex?.deploymentId ? (
                <SecurityChip
                  icon={CheckCircle2}
                  label={`Deployment ${workflow.convex.deploymentId.slice(0, 12)}`}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <section>
          <SectionHeader label="Overview" />
          <div className="rounded-lg border border-border/70 bg-background/45 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Repeat className="size-3.5" />
                {scheduleExpressionLabel(workflow)}
                <span className="text-muted-foreground/50">
                  {workflow.schedule.timezone ?? "UTC"}
                </span>
              </span>
              <Select
                value={schedulePresetId(workflow.schedule)}
                onValueChange={(value) => {
                  const preset = SCHEDULE_PRESETS.find((entry) => entry.id === value);
                  if (preset) props.onEditSchedule(preset.schedule);
                }}
                disabled={props.updating}
              >
                <SelectButton size="xs" variant="default" className="h-7 text-xs">
                  {props.updating ? "Saving…" : "Edit schedule"}
                </SelectButton>
                <SelectPopup>
                  {SCHEDULE_PRESETS.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.schedule.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <SecurityChip
                icon={
                  agentWorkflowRuntimeTargetRunsOffline(security.runtimeTarget) ? Cloud : Server
                }
                label={agentWorkflowRuntimeTargetLabel(security.runtimeTarget)}
              />
              {security.approvalRequiredForWrites ? (
                <SecurityChip icon={ShieldCheck} label="Approve writes" tone="warning" />
              ) : null}
              {security.approvalRequiredForCustomerFacingActions ? (
                <SecurityChip icon={ShieldCheck} label="Approve customer contact" tone="warning" />
              ) : null}
              <SecurityChip
                icon={Box}
                label={security.sandboxAllowed ? "Sandbox on" : "Sandbox off"}
              />
              <SecurityChip icon={Clock3} label={`${security.maxRunMinutes}m max`} />
              {security.allowedToolIds.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">
                        <Wrench className="size-3" />
                        {security.allowedToolIds.length} tools
                      </span>
                    }
                  />
                  <TooltipPopup>{security.allowedToolIds.join(", ")}</TooltipPopup>
                </Tooltip>
              ) : null}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {workflow.executionKind === "convex" &&
              workflow.convex?.deploymentStatus === "draft" ? (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => props.onValidateConvex(workflow)}
                >
                  Validate Convex
                </Button>
              ) : null}
              {workflow.executionKind === "convex" &&
              workflow.convex?.deploymentStatus === "approval-required" ? (
                <Button size="xs" variant="outline" onClick={() => props.onApproveConvex(workflow)}>
                  Approve deployment
                </Button>
              ) : null}
              {workflow.executionKind === "convex" &&
              workflow.convex?.deploymentStatus === "approved" ? (
                <Button size="xs" onClick={() => props.onDeployConvex(workflow)}>
                  Deploy Convex
                </Button>
              ) : null}
            </div>
            {workflow.executionKind === "convex" ? (
              <ConvexDeploymentSecrets
                deploymentRef={workflow.convex?.deploymentRef || "default"}
                onSetSecret={props.onSetConvexSecret}
                onLoadStatus={props.onLoadConvexSecretStatus}
              />
            ) : null}
            {props.convexDeployNotice ? (
              <p className="mt-2 text-xs text-destructive">{props.convexDeployNotice}</p>
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 flex-1 gap-1.5 text-xs"
                disabled={props.running || workflow.status === "archived"}
                onClick={props.onRunOnce}
              >
                <Play className="size-3.5" />
                {props.running ? "Running…" : "Run once"}
              </Button>
            </div>
          </div>
        </section>

        <section>
          <SectionHeader label="Definition" />
          <p className="rounded-lg border border-border/70 bg-background/45 p-3 text-xs leading-relaxed text-muted-foreground">
            {workflow.naturalLanguageDefinition}
          </p>
          <Collapsible>
            <CollapsibleTrigger className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground data-panel-open:[&>svg]:rotate-90">
              <ChevronRight className="size-3.5 transition-transform" />
              Compact skill
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-1.5 overflow-x-auto rounded-lg border border-border/60 bg-muted/30 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
                {workflow.compactSkillDefinition}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        </section>

        <section>
          <SectionHeader label="Steps" trailing={`${workflow.steps.length}`} />
          <ol className="space-y-1.5">
            {workflow.steps.map((step, index) => {
              const presentation = stepKindPresentation(step.kind);
              const Icon = presentation.icon;
              return (
                <li
                  key={step.id}
                  className="rounded-lg border border-border/70 bg-background/45 p-2.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30 text-[10px] text-muted-foreground">
                      {index + 1}
                    </span>
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                      {step.title}
                    </span>
                    {step.toolId ? (
                      <code className="shrink-0 rounded bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {step.toolId}
                      </code>
                    ) : null}
                  </div>
                  <p className="mt-1 pl-7 text-[11px] leading-relaxed text-muted-foreground">
                    {step.instruction}
                  </p>
                  {step.approvalReason ? (
                    <p className="mt-1 pl-7 text-[11px] text-amber-700 dark:text-amber-400">
                      {step.approvalReason}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>

        <section>
          <SectionHeader label="Runs" trailing={`${props.runs.length}`} />
          {props.runs.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border/70 bg-muted/20 p-3 text-xs text-muted-foreground">
              No runs yet. Use “Run once” to start one on the server runner.
            </p>
          ) : (
            <div className="space-y-1.5">
              {props.runs.map((run) => (
                <WorkflowRunRow
                  key={run.id}
                  run={run}
                  busy={props.busyRunId === run.id}
                  onResolve={props.onResolve}
                  onCancel={props.onCancel}
                />
              ))}
            </div>
          )}
        </section>

        <p className="text-[10px] leading-relaxed text-muted-foreground/60">
          Local Codex runs use this user's ChatGPT/Codex-authenticated runtime. Cloud Codex runner
          support is the offline scheduling target; approval gates are still resolved from chat.
        </p>
      </div>
    </ScrollArea>
  );
}

function ActivityView(props: {
  runs: ReadonlyArray<AgentWorkflowRunRecord>;
  definitions: ReadonlyArray<AgentWorkflowDefinition>;
  filter: RunStatusFilter;
  onFilterChange: (filter: RunStatusFilter) => void;
  busyRunId: string | null;
  onResolve: (run: AgentWorkflowRunRecord, decision: AgentWorkflowApprovalDecision) => void;
  onCancel: (run: AgentWorkflowRunRecord) => void;
  storeLoaded: boolean;
}) {
  const nameByWorkflowId = useMemo(() => {
    const map = new Map<string, string>();
    for (const workflow of props.definitions) map.set(workflow.id, workflow.name);
    return map;
  }, [props.definitions]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border/50 px-3 py-2">
        <SegmentedControl
          options={RUN_STATUS_FILTERS}
          value={props.filter}
          onChange={props.onFilterChange}
        />
      </div>
      {props.runs.length === 0 ? (
        <Empty>
          <EmptyTitle className="text-base">No activity</EmptyTitle>
          <EmptyDescription>
            {props.filter === "all"
              ? "Workflow runs will appear here once they start."
              : "No runs match this filter."}
          </EmptyDescription>
        </Empty>
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-1.5 p-3">
            {props.runs.map((run) => (
              <WorkflowRunRow
                key={run.id}
                run={run}
                workflowName={nameByWorkflowId.get(run.workflowId) ?? run.inputSummary}
                busy={props.busyRunId === run.id}
                onResolve={props.onResolve}
                onCancel={props.onCancel}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function WorkflowRunRow(props: {
  run: AgentWorkflowRunRecord;
  workflowName?: string;
  busy: boolean;
  onResolve: (run: AgentWorkflowRunRecord, decision: AgentWorkflowApprovalDecision) => void;
  onCancel: (run: AgentWorkflowRunRecord) => void;
}) {
  const { run } = props;
  const status = runStatusPresentation(run.status);
  const StatusIcon = status.icon;
  const duration = formatDuration(run.startedAt, run.completedAt);
  const clock = formatClock(run.completedAt ?? run.startedAt ?? run.scheduledFor);
  const isFailed = run.status === "failed";
  const summary = isFailed
    ? (run.errorSummary ?? run.resultSummary ?? run.inputSummary)
    : (run.resultSummary ?? run.errorSummary ?? run.inputSummary);
  const isWaiting = run.status === "waiting-for-approval";
  const isCancellable = run.status === "queued" || run.status === "running";

  return (
    <Collapsible className="rounded-lg border border-border/70 bg-background/45">
      <div className="flex items-center gap-2 px-3 py-2">
        <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2 text-left data-panel-open:[&>svg]:rotate-90">
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60 transition-transform" />
          <span className={cn("inline-flex shrink-0 items-center", status.className)}>
            <StatusIcon className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            {props.workflowName ? (
              <p className="truncate text-xs font-medium text-foreground">{props.workflowName}</p>
            ) : null}
            <p
              className={cn(
                "truncate text-[11px]",
                isFailed ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {summary}
            </p>
          </div>
        </CollapsibleTrigger>
        <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground/70">
          {clock ? <span>{clock}</span> : null}
          {duration ? <span className="text-muted-foreground/50">{duration}</span> : null}
        </div>
      </div>
      <CollapsibleContent>
        <div className="border-t border-border/50 px-3 py-2.5">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {run.trigger ? (
              <Badge variant="outline" size="sm" className="rounded-md">
                {run.trigger}
              </Badge>
            ) : null}
            {run.runtimeTarget ? (
              <Badge variant="outline" size="sm" className="rounded-md">
                {agentWorkflowRuntimeTargetLabel(run.runtimeTarget)}
              </Badge>
            ) : null}
            {run.convexWorkflowId ? (
              <Badge variant="outline" size="sm" className="rounded-md">
                Convex {run.convexWorkflowId.slice(0, 12)}
              </Badge>
            ) : null}
            {run.convexStep ? (
              <Badge variant="outline" size="sm" className="rounded-md">
                Step: {run.convexStep}
              </Badge>
            ) : null}
            {run.retryCount !== undefined ? (
              <Badge variant="outline" size="sm" className="rounded-md">
                Retries: {run.retryCount}
              </Badge>
            ) : null}
            <Badge variant="outline" size="sm" className="rounded-md">
              v{run.workflowVersion}
            </Badge>
          </div>
          {isFailed && run.errorSummary ? (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-destructive">Run failed</p>
                <p className="mt-0.5 text-[11px] leading-relaxed break-words text-destructive/90">
                  {run.errorSummary}
                </p>
              </div>
            </div>
          ) : null}
          {run.events.length > 0 ? (
            <ol className="space-y-2">
              {run.events.map((event) => {
                const presentation = runEventPresentation(event.type);
                const Icon = presentation.icon;
                return (
                  <li key={event.id} className="flex items-start gap-2">
                    <Icon className={cn("mt-0.5 size-3.5 shrink-0", presentation.className)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] leading-relaxed text-foreground">{event.message}</p>
                      <p className="text-[10px] text-muted-foreground/60">
                        {formatClock(event.at) ?? event.at}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="text-[11px] text-muted-foreground">No recorded events.</p>
          )}
          {isWaiting || isCancellable ? (
            <div className="mt-3 flex items-center gap-2">
              {isWaiting ? (
                <>
                  <Button
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    disabled={props.busy}
                    onClick={() => props.onResolve(run, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    disabled={props.busy}
                    onClick={() => props.onResolve(run, "decline")}
                  >
                    Decline
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={props.busy}
                  onClick={() => props.onCancel(run)}
                >
                  Cancel run
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="inline-flex size-7 items-center justify-center text-muted-foreground/50">
                      <MessageSquare className="size-3.5" />
                    </span>
                  }
                />
                <TooltipPopup>
                  Resolving here records the run; live tool approvals stay in chat.
                </TooltipPopup>
              </Tooltip>
            </div>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default WorkflowSidebar;
